import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Box,
  StepLabel,
  Step,
  Typography,
  Button,
  Stepper,
  useTheme,
  styled,
  LinearProgress,
  Link,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import { FormProvider } from '../../../components/hook-form';

import { countryData, getCountryCodeFromName, getDataByCountryName, ICountry } from '../../../utils/countryData';
import { PATH_AUTH, PATH_DASHBOARD } from '../../../routes/paths';
import { validationSchema } from '../validation';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  defaultValues,
  modifyCompanySizeData,
  steps,
} from './registerFormConfig';
import Step1 from './Step1';
import Step2 from './Step2';
import InvitationAcceptanceFields from './InvitationAcceptanceFields';
import { BASE_URL } from "../../../config";
import useEmbedded from "../../../hooks/useEmbedded";
import FormHeader from 'src/pages/auth/FormHeader';
import useCarbonUser from 'src/hooks/useCarbonUser';
import { customerUserApi } from "../../../api/customerUserApi";

// ----------------------------------------------------------------------

type FormValuesProps = {
  email: string;
  password: string;
  firstName: string;
  businessName: string;
  country: string;
  industryCode: string;
  companySize: string;
  afterSubmit?: string;
};

interface ISignUpResponse {
  message: string;
  error: boolean;
  created: boolean;
}

interface IRegisterFormProps {
  localStorageAccessible: boolean;
  handleSignInEmbeddedStorageInaccessibleClick?: () => void;
}

const StyledRegisterForm = styled(Box)(({ theme }) => ({
  '& .MuiStepper-root': {
    width: '100%',
    margin: '0 auto',
    [theme.breakpoints.up('sm')]: {
      width: '60%',
    },
  },
}));

export default function RegisterForm({
  localStorageAccessible,
  handleSignInEmbeddedStorageInaccessibleClick,
}: IRegisterFormProps) {
  const { register, isAuthenticated, logout } = useAuth();
  const { getCarbonUser } = useCarbonUser();
  const { isEmbedded, postMessageToParent } = useEmbedded();
  const navigate = useNavigate();

  const isMountedRef = useIsMountedRef();
  const theme = useTheme();

  const [loading, setLoading] = useState<boolean>(false);
  const [countryOptions, setCountryOptions] = useState<ICountry[]>([]);
  const [showPassword, setShowPassword] = useState(false);
  const [acquisitionData, setAcquisitionData] = useState<any>();

  const [isSubmittingAgreement, setIsSubmittingAgreement] = useState(false);
  const [isSendMonthlyNewsletter, setIsSendMonthlyNewsletter] = useState(false);

  const { search } = useLocation();
  const queryString = search;
  const searchParams = new URLSearchParams(search);
  const emailParameter = searchParams.get('email') || defaultValues.email;
  const firstNameParameter = searchParams.get('firstName') || defaultValues.firstName;
  const businessNameParameter = searchParams.get('businessName') || defaultValues.businessName;
  const invitedByMerchantCodeParameter = searchParams.get('invitedByMerchantCode');
  const invitedByMerchantNameParameter = searchParams.get('invitedByMerchantName');

  const [isExistingUser, setIsExistingUser] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(true);

  const isInvitationAcceptace = !!(emailParameter && invitedByMerchantCodeParameter);

  useEffect(() => {
    if(isAuthenticated && !loading && isInvitationAcceptace) {
      logout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loading]);

  useEffect(() => {
    const decodedEmail = emailParameter ? decodeURIComponent(emailParameter).replace(/\s/g, '+') : null;
    if (decodedEmail) {
      customerUserApi(decodedEmail, true)
        .then((res: any) => {
          if(res.data.loginMethods && res.data.loginMethods.length > 0) {
            navigate({ pathname: PATH_AUTH.login, search: queryString }, { replace: true });
          }
        })
        .catch((err: any) => {
          setIsExistingUser(false);
        })
        .finally(() => {
          setIsLoadingUser(false);
        })
    } else {
      setIsLoadingUser(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailParameter, getCarbonUser, queryString]);

  const handleTermsAgreementChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSubmittingAgreement(event.target.checked);
  };

  const handlePromotionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSendMonthlyNewsletter(event.target.checked);
  };

  const [signUpResponse, setSignUpResponse] = useState<ISignUpResponse>({
    message: "",
    error: false,
    created: false
  });

  const methods = useForm<FormValuesProps>({
    mode: 'onChange',
    resolver: yupResolver(isInvitationAcceptace ? validationSchema.RegisterAcceptInvitation : validationSchema.Register),
    defaultValues,
  });

  const {
    reset,
    setError,
    handleSubmit,
    getValues,
    formState: { errors, isValid },
    setValue,
    watch,
    trigger
  } = methods;

  const name = watch('email');

  useEffect(() => {
    if (name) {
      setSignUpResponse((prev: any) => ({ ...prev, message: '' }));
    }
  }, [name]);

  useEffect(() => {
    if(emailParameter) {
      setValue('email', emailParameter);
      trigger("email");
    }
    if(firstNameParameter) {
      setValue('firstName', firstNameParameter);
      trigger("firstName");
    }
    if(businessNameParameter) {
      setValue('businessName', businessNameParameter);
      trigger("businessName");
    }
  }, [emailParameter, firstNameParameter, businessNameParameter, setValue, trigger])

  useEffect(() => {
    const options: any[] = countryData.countries.map(country => {
      return { ...country, label: country.name }
    });
    setCountryOptions(options);
  }, []);

  useEffect(() => {
    const acquisitionData = {
      registerUrl: window.location?.href,
      registerReferrerUrl: window.document?.referrer
    };
    setAcquisitionData(acquisitionData);
  }, []);

  const onSubmit = async (data: FormValuesProps) => {
    try {
      setLoading(true)
      if (Object.keys(errors).length === 0) {
        const country = getValues('country');
        const currency = getDataByCountryName(country)?.currency;

        const createMerchantData = {
          merchantName: data.businessName,
          merchantCountry: getCountryCodeFromName(country),
          platform: "BUSINESS_PORTAL",
          currency: currency,
          merchantReferrerCode: 'CARBONCLICK',
          industryCode: data?.industryCode,
          companySize: modifyCompanySizeData(data?.companySize)
        };
        const createCustomerData = {
          marketingOptIn: isSendMonthlyNewsletter,
          invitedByMerchantCode: invitedByMerchantCodeParameter
        }

        if (isInvitationAcceptace) {
          await register(
            data.email,
            data.password,
            data.firstName,
            createCustomerData,
            acquisitionData,
            undefined,
          )
            .then((res) => {
              setSignUpResponse((prev) => ({ ...prev, error: false, created: true }));
              navigate({ pathname: PATH_DASHBOARD.root },  { replace: true, state: { inviteFlow: true } });
              mixpanel.track('Sign up');
              // @ts-ignore
              gtag('event', 'portal_registration');
            })
            .catch(error => {
              let { message } = error;
              let errorFromPreSignUp = /^PreSignUp failed with error (.+)/.exec(message);
              if (errorFromPreSignUp) {
                message = errorFromPreSignUp[1];
              }
              setSignUpResponse({ message, error: true, created: false });
              console.error(error);
              setLoading(false);
            })
        } else {
          await register(
            data.email,
            data.password,
            data.firstName,
            createCustomerData,
            acquisitionData,
            createMerchantData,
          )
            .then((res) => {
              setSignUpResponse((prev) => ({ ...prev, error: false, created: true }));
              if (res && res.userConfirmed === false) {
                navigate(
                  { pathname: PATH_AUTH.verify, search: `?email=${encodeURIComponent(data.email)}` },
                  { replace: true }
                );
              }
              mixpanel.track('Sign up');
              // @ts-ignore
              gtag('event', 'portal_registration');
            })
            .catch(error => {
              setSignUpResponse({ message: error.message, error: true, created: false });
              console.error(error);
              setLoading(false);
            })
        }
      }
    } catch (error) {
      console.error(error);
      setLoading(false);
      reset();
      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  const [activeStep, setActiveStep] = useState(0);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleStorageInaccessibleGo = () => {
    console.log('Navigating to the root')

    const modifiedURL = addRegisterAndUTMParametersToURL(`${BASE_URL}${PATH_AUTH.register}`);

    console.log(modifiedURL)
    postMessageToParent({
      type: 'PORTAL_SIGNED_UP',
      payload: modifiedURL
    });
  };

  function addRegisterAndUTMParametersToURL(targetURL:string) {
    // Get the current page's URL
    var currentPageURL = window.location.href;

    // Parse the current page's URL to extract query parameters
    var urlParser = new URL(currentPageURL);
    var queryParams = urlParser.searchParams;

    // Filter out parameters starting with 'utm'
    var utmParams = Array.from(queryParams.entries())
        .filter(([key, _]) => key.startsWith('utm'));

    // Parse the target URL
    var targetParser = new URL(targetURL);

    targetParser.searchParams.set('email', getValues('email'));
    targetParser.searchParams.set('firstName', getValues('firstName'));
    targetParser.searchParams.set('businessName', getValues('businessName'));

    // Add the 'utm' parameters to the target URL's query string
    utmParams.forEach(([key, value]) => {
      targetParser.searchParams.set(key, value);
    });

    // Return the modified target URL
    return targetParser.href;
  }

  const disableStorageInaccessibleStep =
    !getValues('firstName') ||
    !getValues('email') ||
    !getValues('businessName') ||
    !!errors.firstName ||
    !!errors.email ||
    !!errors.businessName;

  const disableFirstStep =
    !getValues('firstName') ||
    !getValues('email') ||
    !getValues('businessName') ||
    !getValues('password') ||
    !!errors.firstName ||
    !!errors.email ||
    !!errors.businessName ||
    !!errors.password;

  const disableSecondStep =
    !getValues('country') ||
    !!errors.country ||
    !getValues('industryCode') ||
    !!errors.industryCode ||
    !getValues('companySize') ||
    !!errors.companySize ||
    !isSubmittingAgreement;    

  const registerFormTitle = isInvitationAcceptace
    ? <>Join the {invitedByMerchantNameParameter} account</>
    : <>Sign up for free, <br /> no credit card required.</>;

  if (isLoadingUser) {
    return <LinearProgress />
  }

  return (
    <>
      <FormHeader title={registerFormTitle} />

      {isInvitationAcceptace && invitedByMerchantNameParameter && (
        <>
          {isExistingUser ? (
            <Typography>
              It looks like you already have a CarbonClick account. Please verify your password and
              continue to link your account.
            </Typography>
          ) : (
            <Typography>
              You’ve been invited to join the {invitedByMerchantNameParameter} account. 
              Please complete your profile to continue.
            </Typography>
          )}
        </>
      )}

    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <StyledRegisterForm>

        <Box sx={{ width: '100%' }}>
          {(isEmbedded && localStorageAccessible && !isInvitationAcceptace) &&
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps?.map((label) => (
                <Step key={label}>
                  <StepLabel sx={{
                    '& .MuiStepIcon-root.Mui-active': {
                      color:
                        (activeStep === 0 && !disableFirstStep) ||
                          (activeStep === 1 && !disableSecondStep)
                             ? theme?.palette.primary.main
                               : '#919EAB',
                    },
                  }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          }

          {isInvitationAcceptace ? (
            <InvitationAcceptanceFields
              isSubmittingAgreement={isSubmittingAgreement}
              handleTermsAgreementChange={handleTermsAgreementChange}
              handlePromotionChange={handlePromotionChange}
              errors={errors}
              isSendMonthlyNewsletter={isSendMonthlyNewsletter}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              localStorageAccessible={localStorageAccessible}
              isInvitationAcceptace={!!isInvitationAcceptace}
              isExistingUser={isExistingUser}
            />

          ) : activeStep === 0 ? (
            <Step1
              errors={errors}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              localStorageAccessible={localStorageAccessible}
            />
          ) : activeStep === 1 ? (
            <Step2
              countryOptions={countryOptions}
              isSubmittingAgreement={isSubmittingAgreement}
              handleTermsAgreementChange={handleTermsAgreementChange}
              handlePromotionChange={handlePromotionChange}
              errors={errors}
              isSendMonthlyNewsletter={isSendMonthlyNewsletter}
            />
          ) : null
          }
        </Box>

        {signUpResponse.error ? (
          <Typography variant="body2" color='error'>
            {signUpResponse.message}
          </Typography>
        ) : null}

        <Box sx={{
          display: 'flex',
          justifyContent: activeStep === 0 ? 'end' : 'space-between',
          alignItems: 'center',
          mt: 3
        }}>
          {(!localStorageAccessible && isEmbedded) ?
              <Button
                  variant="contained"
                  onClick={handleStorageInaccessibleGo}
                  disabled={disableStorageInaccessibleStep}
                  type="submit"
              >
                Go
              </Button>
              : (activeStep < steps.length - 1 && !isInvitationAcceptace) ? (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={disableFirstStep}
              type="submit"
              fullWidth
              size='small'
            >
              Next
            </Button>
          ) : (
            <LoadingButton
              variant="contained"
              type="submit"
              loading={loading}
              disabled={!isSubmittingAgreement || !isValid}
              fullWidth
              size='small'
            >
              {isInvitationAcceptace ? 'Join account' : 'Sign up'}
            </LoadingButton>
          )}
        </Box>

      </StyledRegisterForm>
    </FormProvider>

    {!isInvitationAcceptace && (
      <Typography align='center' variant='body2'>
        Already have an account?{' '}
        {isEmbedded && !localStorageAccessible
          ? <Link component={Typography} onClick={handleSignInEmbeddedStorageInaccessibleClick}>
              Sign in.
            </Link>
          : <Link component={RouterLink} to={PATH_AUTH.login}>Sign in.</Link>
        }
      </Typography>
    )}
    </>
  );
}
